import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  useMediaQuery,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Cell } from "react-table";
import { loadLogs } from "redux/transactions/transactionsFunctions";
import { RootState } from "redux/rootReducer";
import * as TransactionsTypes from "types/Transactions";
import { dateToString } from "utility/date";
import { asDecimal, asEur, seperateThousands } from "utility/numbers";
import { totalCosts } from "utility/totalCosts";
import { camelToSnakeCase } from "utility/convertCase";
import DateRangeSelector from "components/DateRangeSelector";
import NoTransactions from "components/NoTransactions";
import { generateFilename } from "../utility/files";
import PortfolioName from "../components/PortfolioName";
import ExportExcel from "../components/ExportExcel";
import Table from "../components/Table";

type FooterEntry = [string, TransactionsTypes.Sums | undefined];

const Logs = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const matchesMediaQuery = useMediaQuery("(min-width: 960px)");

  const reportControlId = useSelector(
    (state: RootState) => state.user.reportControlId
  );

  const transactionsData = useSelector(
    (state: RootState) => state.transactions.data ?? []
  );

  const transactionsLoading = useSelector(
    (state: RootState) => state.transactions.loading
  );

  const startDate = useSelector((state: RootState) => state.user.startDate);

  const endDate = useSelector((state: RootState) => state.user.reportDate);

  React.useEffect(() => {
    if (reportControlId && startDate && endDate) {
      dispatch(loadLogs(reportControlId, startDate, endDate));
    }
  }, [dispatch, reportControlId, startDate, endDate]);

  return (
    <Grid
      container
      justifyContent={matchesMediaQuery ? "center" : "flex-start"}
    >
      <Grid item container md={10} sm={12}>
        {matchesMediaQuery ? (
          <Grid item xs={9}>
            <h1 style={{ color: "#003366" }}>{t("logs")}</h1>
          </Grid>
        ) : (
          <></>
        )}
        <Grid container item md={3} sm={12} alignContent="center">
          <DateRangeSelector />
        </Grid>
      </Grid>
      <Grid item container md={10} style={{ padding: "1em 0" }}>
        {transactionsData.length > 0 ? (
          transactionsData.map(
            (data: TransactionsTypes.Portfolio, index: number) => {
              const isExpandedDefault = index === 0;
              const footerEntries: FooterEntry[] = Object.entries(
                data.Sums
              ).filter(([, value]: FooterEntry) => value !== undefined);

              const tableColumns = [
                {
                  Header: t("transaction_main_type"),
                  accessor: "TransactionMainType",
                  paddingRight: "10px",
                  Footer: footerEntries.map((entry) => (
                    <div key={`${data.PortfolioId}-${entry[0]}`}>
                      <b>{t(camelToSnakeCase(entry[0]))}</b>
                    </div>
                  )),
                  fontFamily: "Lato light",
                },
                {
                  Header: t("transaction_date"),
                  accessor: "TransactionDate",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                },
                {
                  Header: t("clearing_date"),
                  accessor: "ClearingDate",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                },
                {
                  Header: t("transaction_number"),
                  accessor: "TransactionNumber",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                },
                {
                  Header: t("instrument_name"),
                  accessor: "InstrumentName",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                },
                {
                  Header: t("count"),
                  accessor: "Count",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (cell: Cell<TransactionsTypes.Transaction, number>) =>
                    seperateThousands(cell.value, 4),
                  textAlign: "right",
                },
                {
                  Header: t("price"),
                  accessor: "Price",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (cell: Cell<TransactionsTypes.Transaction, number>) =>
                    `${asDecimal(cell.value, 4)} / ${t("pcs")}`,
                  textAlign: "right",
                  Footer: footerEntries.map((entry) => (
                    <div key={`${data.PortfolioId}-${entry[0]}Sum`}>
                      {asEur(entry[1]?.CleanAmount, 2)}
                    </div>
                  )),
                },
                {
                  Header: t("fee"),
                  accessor: "Fee",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (cell: Cell<TransactionsTypes.Transaction, number>) =>
                    asEur(cell.value, 2),
                  textAlign: "right",
                  Footer: footerEntries.map((entry) => (
                    <div key={`${data.PortfolioId}-${entry[0]}Sum`}>
                      {asEur(entry[1]?.Fee, 2)}
                    </div>
                  )),
                },
                {
                  Header: t("tax"),
                  accessor: "Tax",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (cell: Cell<TransactionsTypes.Transaction, number>) =>
                    asEur(cell.value, 2),
                  textAlign: "right",
                  Footer: footerEntries.map((entry) => (
                    <div key={`${data.PortfolioId}-${entry[0]}Sum`}>
                      {asEur(entry[1]?.Tax, 2)}
                    </div>
                  )),
                },
                {
                  Header: t("total_costs"),
                  accessor: "TotalCosts",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (cell: Cell<TransactionsTypes.Transaction, number>) =>
                    asEur(cell.value, 2),
                  textAlign: "right",
                  Footer: footerEntries.map((entry) => (
                    <div key={`${data.PortfolioId}-${entry[0]}Sum`}>
                      {asEur(entry[1]?.TotalCosts, 2)}
                    </div>
                  )),
                },
                {
                  Header: t("dirty_amount"),
                  accessor: "DirtyAmount",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (cell: Cell<TransactionsTypes.Transaction, number>) =>
                    asEur(cell.value, 2),
                  textAlign: "right",
                  Footer: footerEntries.map((entry) => (
                    <div key={`${data.PortfolioId}-${entry[0]}Sum`}>
                      {asEur(entry[1]?.DirtyAmount, 2)}
                    </div>
                  )),
                },
              ];

              const tableData = data.Transactions.map((transaction) => ({
                ...transaction,
                ClearingDate: dateToString(transaction.ClearingDate),
                TransactionDate: dateToString(transaction.TransactionDate),
                TotalCosts: totalCosts(
                  transaction.ServiceFee,
                  transaction.Fee,
                  transaction.Tax
                ),
              }));

              const excelHeaders = [
                tableColumns.map((column) => column.Header),
              ];

              const excelData = data.Transactions.map((transaction) => ({
                TransactionMainType: transaction.TransactionMainType,
                TransactionDate: dateToString(transaction.TransactionDate),
                ClearingDate: dateToString(transaction.ClearingDate),
                TransactionNumber: transaction.TransactionNumber.toString(),
                InstrumentName: transaction.InstrumentName,
                Count: transaction.Count,
                Price: transaction.Price,
                Fee: transaction.Fee,
                Tax: transaction.Tax,
                TotalCosts: totalCosts(
                  transaction.ServiceFee,
                  transaction.Fee,
                  transaction.Tax
                ),
                DirtyAmount: transaction.DirtyAmount,
              }));

              const excelSums = footerEntries.map((entry) => ({
                TransactionMainType: t(camelToSnakeCase(entry[0])),
                TransactionDate: "",
                ClearingDate: "",
                TransactionNumber: "",
                InstrumentName: "",
                Count: 0,
                Price: entry[1]?.CleanAmount ?? 0,
                Fee: entry[1]?.Fee ?? 0,
                Tax: entry[1]?.Tax ?? 0,
                TotalCosts: entry[1]?.TotalCosts ?? 0,
                DirtyAmount: entry[1]?.DirtyAmount ?? 0,
              }));
              excelSums.forEach((footer) => excelData.push(footer));

              return (
                <Grid
                  key={data.PortfolioId}
                  item
                  xs={12}
                  justifyContent="center"
                  style={{
                    borderBottom: "1px solid #4A76A1",
                    minWidth: "inherit",
                  }}
                >
                  <Accordion
                    defaultExpanded={isExpandedDefault}
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      style={{ padding: 0 }}
                    >
                      <PortfolioName name={data.PortfolioName} />
                    </AccordionSummary>
                    <ExportExcel
                      data={excelData}
                      headers={excelHeaders}
                      filename={generateFilename(
                        t("logs"),
                        data.PortfolioName,
                        startDate,
                        endDate
                      )}
                      matchesMediaQuery={matchesMediaQuery}
                    />
                    <AccordionDetails
                      style={{
                        maxWidth: "inherit",
                        overflowY: "hidden",
                        padding: matchesMediaQuery
                          ? "8px 16px 16px"
                          : "8px 0 16px 0",
                      }}
                    >
                      <Table
                        columns={tableColumns}
                        data={tableData}
                        loading={transactionsLoading}
                        hasFooter
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            }
          )
        ) : (
          <NoTransactions />
        )}
      </Grid>
    </Grid>
  );
};

export default React.memo(Logs);
